import React, { useState } from 'react';

const TeamSettings = () => {
  const [activeTab, setActiveTab] = useState('members');
  
  const [teamMembers] = useState([
    {
      id: 1,
      name: 'John Smith',
      email: 'john.smith@company.com',
      role: 'Admin',
      status: 'active',
      lastActive: '2 hours ago',
      avatar: 'JS'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@company.com',
      role: 'Developer',
      status: 'active',
      lastActive: '5 minutes ago',
      avatar: 'SJ'
    },
    {
      id: 3,
      name: 'Mike Chen',
      email: 'mike.chen@company.com',
      role: 'Analyst',
      status: 'inactive',
      lastActive: '2 days ago',
      avatar: 'MC'
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@company.com',
      role: 'Developer',
      status: 'active',
      lastActive: '1 hour ago',
      avatar: 'ED'
    }
  ]);

  const [permissions] = useState([
    { id: 1, name: 'Create Workflows', description: 'Allow users to create and edit workflows', admin: true, developer: true, analyst: false },
    { id: 2, name: 'Deploy AI Agents', description: 'Permission to deploy and manage AI agents', admin: true, developer: true, analyst: false },
    { id: 3, name: 'View Analytics', description: 'Access to analytics and reporting features', admin: true, developer: true, analyst: true },
    { id: 4, name: 'Manage Team', description: 'Add, remove, and modify team member permissions', admin: true, developer: false, analyst: false },
    { id: 5, name: 'System Settings', description: 'Access to system configuration and settings', admin: true, developer: false, analyst: false },
    { id: 6, name: 'API Access', description: 'Generate and manage API keys', admin: true, developer: true, analyst: false }
  ]);

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'text-red-500 bg-red-500/10';
      case 'Developer': return 'text-blue-500 bg-blue-500/10';
      case 'Analyst': return 'text-purple-500 bg-purple-500/10';
      default: return 'text-slate-500 bg-slate-500/10';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-emerald-500 bg-emerald-500/10';
      case 'inactive': return 'text-slate-500 bg-slate-500/10';
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      default: return 'text-slate-500 bg-slate-500/10';
    }
  };

  const tabs = [
    { id: 'members', label: 'Team Members', icon: 'group' },
    { id: 'roles', label: 'Roles & Permissions', icon: 'admin_panel_settings' },
    { id: 'settings', label: 'Team Settings', icon: 'settings' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-black text-white tracking-tight">Team Settings</h2>
          <p className="text-[#92b7c9] mt-1">Manage your team members, roles, and permissions.</p>
        </div>
        <button className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors">
          <span className="material-symbols-outlined">person_add</span>
          Invite Member
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-[#111c22] border border-slate-200 dark:border-[#233c48] rounded-xl overflow-hidden">
        <div className="border-b border-slate-200 dark:border-[#233c48]">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5'
                    : 'text-slate-400 hover:text-white hover:bg-slate-50 dark:hover:bg-[#1a2b35]'
                }`}
              >
                <span className="material-symbols-outlined text-sm">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Team Members Tab */}
          {activeTab === 'members' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-white font-bold text-lg">Team Members ({teamMembers.length})</h3>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">search</span>
                    <input 
                      className="bg-slate-100 dark:bg-[#233c48] border-none rounded-lg pl-9 pr-4 py-2 text-sm focus:ring-1 focus:ring-primary text-white placeholder:text-slate-500 w-64" 
                      placeholder="Search members..." 
                      type="text"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {teamMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-[#1a2b35] rounded-lg hover:bg-slate-100 dark:hover:bg-[#233c48] transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold">
                        {member.avatar}
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">{member.name}</h4>
                        <p className="text-slate-400 text-sm">{member.email}</p>
                        <p className="text-slate-500 text-xs">Last active: {member.lastActive}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${getRoleColor(member.role)}`}>
                        {member.role}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(member.status)}`}>
                        {member.status}
                      </span>
                      <button className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-[#233c48] transition-colors">
                        <span className="material-symbols-outlined text-sm">more_vert</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Roles & Permissions Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-bold text-lg mb-4">Role Permissions</h3>
                <p className="text-slate-400 text-sm mb-6">Configure what each role can access and modify.</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-[#233c48]">
                      <th className="text-left py-3 text-white font-semibold">Permission</th>
                      <th className="text-center py-3 text-white font-semibold">Admin</th>
                      <th className="text-center py-3 text-white font-semibold">Developer</th>
                      <th className="text-center py-3 text-white font-semibold">Analyst</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((permission) => (
                      <tr key={permission.id} className="border-b border-slate-100 dark:border-[#233c48] hover:bg-slate-50 dark:hover:bg-[#1a2b35] transition-colors">
                        <td className="py-4">
                          <div>
                            <p className="text-white font-medium">{permission.name}</p>
                            <p className="text-slate-400 text-sm">{permission.description}</p>
                          </div>
                        </td>
                        <td className="py-4 text-center">
                          <div className={`w-6 h-6 rounded-full mx-auto flex items-center justify-center ${
                            permission.admin ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                          }`}>
                            {permission.admin && <span className="material-symbols-outlined text-white text-sm">check</span>}
                          </div>
                        </td>
                        <td className="py-4 text-center">
                          <div className={`w-6 h-6 rounded-full mx-auto flex items-center justify-center ${
                            permission.developer ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                          }`}>
                            {permission.developer && <span className="material-symbols-outlined text-white text-sm">check</span>}
                          </div>
                        </td>
                        <td className="py-4 text-center">
                          <div className={`w-6 h-6 rounded-full mx-auto flex items-center justify-center ${
                            permission.analyst ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                          }`}>
                            {permission.analyst && <span className="material-symbols-outlined text-white text-sm">check</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Team Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-bold text-lg mb-4">General Settings</h3>
                <p className="text-slate-400 text-sm mb-6">Configure team-wide settings and preferences.</p>
              </div>

              <div className="space-y-6">
                {/* Organization Info */}
                <div className="bg-slate-50 dark:bg-[#1a2b35] rounded-lg p-6">
                  <h4 className="text-white font-semibold mb-4">Organization Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm font-medium mb-2">Organization Name</label>
                      <input 
                        type="text" 
                        defaultValue="Global-Tech Inc." 
                        className="w-full bg-white dark:bg-[#233c48] border border-slate-200 dark:border-[#325567] rounded-lg px-4 py-2 text-white focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm font-medium mb-2">Industry</label>
                      <select className="w-full bg-white dark:bg-[#233c48] border border-slate-200 dark:border-[#325567] rounded-lg px-4 py-2 text-white focus:ring-1 focus:ring-primary">
                        <option>Technology</option>
                        <option>Finance</option>
                        <option>Healthcare</option>
                        <option>Manufacturing</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Security Settings */}
                <div className="bg-slate-50 dark:bg-[#1a2b35] rounded-lg p-6">
                  <h4 className="text-white font-semibold mb-4">Security Settings</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Two-Factor Authentication</p>
                        <p className="text-slate-400 text-sm">Require 2FA for all team members</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Session Timeout</p>
                        <p className="text-slate-400 text-sm">Automatically log out inactive users</p>
                      </div>
                      <select className="bg-white dark:bg-[#233c48] border border-slate-200 dark:border-[#325567] rounded-lg px-3 py-1 text-white text-sm focus:ring-1 focus:ring-primary">
                        <option>30 minutes</option>
                        <option>1 hour</option>
                        <option>4 hours</option>
                        <option>8 hours</option>
                        <option>Never</option>
                      </select>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">IP Restrictions</p>
                        <p className="text-slate-400 text-sm">Limit access to specific IP addresses</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Notification Settings */}
                <div className="bg-slate-50 dark:bg-[#1a2b35] rounded-lg p-6">
                  <h4 className="text-white font-semibold mb-4">Notification Settings</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Email Notifications</p>
                        <p className="text-slate-400 text-sm">Send email alerts for important events</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Slack Integration</p>
                        <p className="text-slate-400 text-sm">Send notifications to Slack channels</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSettings;